/**
 * Trend Chart — line or area chart for metric trends over periods.
 *
 * Usage:
 *   <TrendChart
 *     data={[{period: "Jan", value: 2100000}, ...]}
 *     unit="USD"
 *     height={250}
 *     showArea
 *   />
 */

import React from 'react';
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { CHART_COLORS, RECHARTS_DEFAULTS, formatMetricValue } from '../../config/chartTheme';

export interface TrendDataPoint {
  period: string;
  value: number;
  [key: string]: any;
}

interface TrendChartProps {
  data: TrendDataPoint[];
  metricName?: string;
  unit?: string;
  height?: number;
  showArea?: boolean;
  color?: string;
  referenceLine?: number;
  referenceLabel?: string;
  onClick?: (point: TrendDataPoint) => void;
}

const CustomTooltip = ({ active, payload, label, unit }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={RECHARTS_DEFAULTS.tooltipContentStyle}>
      <div style={RECHARTS_DEFAULTS.tooltipLabelStyle}>{label}</div>
      <div style={{ color: CHART_COLORS.textPrimary, fontWeight: 600 }}>
        {formatMetricValue(payload[0]?.value, unit || 'USD')}
      </div>
    </div>
  );
};

export const TrendChart: React.FC<TrendChartProps> = ({
  data, unit = 'USD', height = 250,
  showArea = false, color = CHART_COLORS.accent,
  referenceLine, referenceLabel, onClick,
}) => {
  const handleClick = onClick
    ? (e: any) => { if (e?.activePayload?.[0]?.payload) onClick(e.activePayload[0].payload); }
    : undefined;

  const commonChildren = (
    <>
      <CartesianGrid {...RECHARTS_DEFAULTS.gridProps} />
      <XAxis dataKey="period" {...RECHARTS_DEFAULTS.xAxisProps} />
      <YAxis
        {...RECHARTS_DEFAULTS.yAxisProps}
        tickFormatter={(v: number) => formatMetricValue(v, unit)}
      />
      <Tooltip
        content={<CustomTooltip unit={unit} />}
        cursor={RECHARTS_DEFAULTS.tooltipCursor}
      />
      {referenceLine !== undefined && (
        <ReferenceLine
          y={referenceLine}
          stroke={CHART_COLORS.textMuted}
          strokeDasharray="6 3"
          label={{ value: referenceLabel, fill: CHART_COLORS.textMuted, fontSize: 10 }}
        />
      )}
    </>
  );

  const dotProps = { fill: color, strokeWidth: 0, r: 3 };
  const activeDotProps = { r: 5, strokeWidth: 2, stroke: CHART_COLORS.bgBase };

  if (showArea) {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} onClick={handleClick}>
          {commonChildren}
          <Area
            type="monotone" dataKey="value" stroke={color}
            fill={color} fillOpacity={0.1} strokeWidth={2}
            dot={dotProps} activeDot={activeDotProps}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} onClick={handleClick}>
        {commonChildren}
        <Line
          type="monotone" dataKey="value" stroke={color}
          strokeWidth={2} dot={dotProps} activeDot={activeDotProps}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default TrendChart;
